import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import {
  patientService,
  PatientDemographics,
} from "../../services/patient.service";

interface Props {
  patientId: number;
  onUpdate?: (updatedData: any) => void;
}

const PatientDemographicsTab: React.FC<Props> = ({ patientId, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [demographics, setDemographics] = useState<PatientDemographics | null>(
    null,
  );

  useEffect(() => {
    loadDemographics();
  }, [patientId]);

  const loadDemographics = async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientDemographics(patientId);
      setDemographics(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load demographics");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!demographics) return;

    try {
      setSaving(true);
      setSuccess(false);
      setError("");
      await patientService.updatePatientDemographics(patientId, demographics);
      setSuccess(true);
      if (onUpdate) {
        onUpdate({
          firstname: demographics.firstname,
          lastname: demographics.lastname,
          gender: demographics.gender,
          dob: demographics.dob,
          phone1: demographics.phone1,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update demographics");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof PatientDemographics, value: any) => {
    if (!demographics) return;
    setDemographics({ ...demographics, [field]: value });
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!demographics) {
    return <Alert severity='error'>{error || "No data found"}</Alert>;
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Demographics
      </Typography>

      {success && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Demographics updated successfully
        </Alert>
      )}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label='First Name'
            fullWidth
            required
            value={demographics.firstname || ""}
            onChange={(e) => handleChange("firstname", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='Middle Name'
            fullWidth
            value={demographics.middleName || ""}
            onChange={(e) => handleChange("middleName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='Last Name'
            fullWidth
            required
            value={demographics.lastname || ""}
            onChange={(e) => handleChange("lastname", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label='Phone'
            fullWidth
            value={demographics.phone1 || ""}
            onChange={(e) => handleChange("phone1", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='Gender'
            fullWidth
            select
            value={demographics.gender || ""}
            onChange={(e) => handleChange("gender", e.target.value)}
          >
            <MenuItem value=''>Select</MenuItem>
            <MenuItem value='M'>Male</MenuItem>
            <MenuItem value='F'>Female</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='Date of Birth'
            fullWidth
            type='date'
            InputLabelProps={{ shrink: true }}
            value={
              demographics.dob
                ? new Date(demographics.dob).toISOString().split("T")[0]
                : ""
            }
            onChange={(e) => handleChange("dob", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label='Country'
            fullWidth
            required
            value={demographics.country || ""}
            onChange={(e) => handleChange("country", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label='Street Address'
            fullWidth
            required
            value={demographics.address1 || ""}
            onChange={(e) => handleChange("address1", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Street Address 2'
            fullWidth
            value={demographics.address2 || ""}
            onChange={(e) => handleChange("address2", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label='City'
            fullWidth
            required
            value={demographics.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='State'
            fullWidth
            required
            value={demographics.state || ""}
            onChange={(e) => handleChange("state", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label='Zipcode'
            fullWidth
            required
            value={demographics.zipcode || ""}
            onChange={(e) => handleChange("zipcode", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={demographics.sameShippingAddress || false}
                onChange={(e) =>
                  handleChange("sameShippingAddress", e.target.checked)
                }
              />
            }
            label='Same Shipping Address'
          />
        </Grid>

        {!demographics.sameShippingAddress && (
          <>
            <Grid item xs={12}>
              <Typography variant='subtitle1' color='text.secondary'>
                Shipping Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Shipping Street Address'
                fullWidth
                value={demographics.shippingAddress1 || ""}
                onChange={(e) =>
                  handleChange("shippingAddress1", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Shipping Street Address 2'
                fullWidth
                value={demographics.shippingAddress2 || ""}
                onChange={(e) =>
                  handleChange("shippingAddress2", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Shipping City'
                fullWidth
                value={demographics.shippingCity || ""}
                onChange={(e) => handleChange("shippingCity", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Shipping State'
                fullWidth
                value={demographics.shippingState || ""}
                onChange={(e) => handleChange("shippingState", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Shipping Zipcode'
                fullWidth
                value={demographics.shippingZipcode || ""}
                onChange={(e) =>
                  handleChange("shippingZipcode", e.target.value)
                }
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientDemographicsTab;
